import { useCallback, useMemo } from 'react';
import { useBillingEntities } from '@features/billing-entities';
import { usePatientClinicalInfoQuery } from '@features/patients';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import {
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
} from '../store/api';
import type { EligibilityCheckResponse } from '../types';

export type EligibilityCardState = 'hidden' | 'needs_info' | 'needs_check' | 'check_complete';

type MissingPatientField = 'first_name' | 'last_name' | 'dob' | 'zip';

export interface EligibilityReadiness {
  missingPatientFields: MissingPatientField[];
  missingBillingNpi: boolean;
}

interface UseEligibilityCardStateOptions {
  patientId?: string | null;
  encounterId?: string | null;
}

interface UseEligibilityCardStateResult {
  state: EligibilityCardState;
  eligibilityData: EligibilityCheckResponse | undefined;
  isLoading: boolean;
  runCheck: () => void;
  isRunning: boolean;
  readiness: EligibilityReadiness;
}

const EMPTY_READINESS: EligibilityReadiness = {
  missingPatientFields: [],
  missingBillingNpi: false,
};

export function useEligibilityCardState({
  patientId,
  encounterId,
}: UseEligibilityCardStateOptions): UseEligibilityCardStateResult {
  // Check feature gate
  const isEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);

  // ── Readiness queries ────────────────────────────────────────────────

  // Patient demographics — RTK Query will cache-hit if parent already fetched
  const { data: clinicalInfoResponse } = usePatientClinicalInfoQuery(
    { patientId: patientId ?? '' },
    { skip: !isEnabled || !patientId },
  );

  // Org billing entities — check for billing NPI
  const { entities: billingEntities } = useBillingEntities({
    skip: !isEnabled,
  });

  // ── Readiness derivation ─────────────────────────────────────────────

  const readiness = useMemo<EligibilityReadiness>(() => {
    if (!isEnabled || !patientId) return EMPTY_READINESS;

    const info = clinicalInfoResponse?.patient_clinical_info;
    const missingPatientFields: MissingPatientField[] = [];

    if (!info?.first_name) missingPatientFields.push('first_name');
    if (!info?.last_name) missingPatientFields.push('last_name');
    if (!info?.dob) missingPatientFields.push('dob');

    if (!info?.primary_address?.zip) missingPatientFields.push('zip');

    const missingBillingNpi = !billingEntities.some(
      (e) => e.is_default && !!e.billing_npi,
    );

    return { missingPatientFields, missingBillingNpi };
  }, [isEnabled, patientId, clinicalInfoResponse, billingEntities]);

  // ── Eligibility queries ──────────────────────────────────────────────

  // Encounter-scoped query: used when encounterId is available
  const {
    data: encounterEligibilityData,
    isLoading: isEncounterLoading,
  } = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !isEnabled || !encounterId },
  );

  // Patient-scoped query: used when only patientId is available (no encounterId)
  const {
    data: patientEligibilityData,
    isLoading: isPatientLoading,
  } = useGetPatientEligibilityQuery(
    { patientId: patientId ?? '' },
    { skip: !isEnabled || !!encounterId || !patientId },
  );

  // Encounter-scoped mutation
  const [triggerEncounterCheck, { isLoading: isEncounterRunning }] =
    useRunEligibilityCheckMutation();

  // Patient-scoped mutation
  const [triggerPatientCheck, { isLoading: isPatientRunning }] =
    useRunPatientEligibilityCheckMutation();

  const runCheck = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else if (patientId) {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  // Prefer encounter-scoped data, fall back to patient-scoped
  const eligibilityData = encounterEligibilityData ?? patientEligibilityData;

  // ── State derivation ─────────────────────────────────────────────────

  const state = useMemo<EligibilityCardState>(() => {
    if (!isEnabled) return 'hidden';
    if (!patientId && !encounterId) return 'hidden';
    if (eligibilityData) return 'check_complete';

    if (readiness.missingPatientFields.length > 0 || readiness.missingBillingNpi) {
      return 'needs_info';
    }

    return 'needs_check';
  }, [isEnabled, patientId, encounterId, eligibilityData, readiness]);

  const isLoading =
    isEnabled &&
    ((!!encounterId && isEncounterLoading) ||
      (!encounterId && !!patientId && isPatientLoading));

  const isRunning = isEncounterRunning || isPatientRunning;

  return {
    state,
    eligibilityData,
    isLoading,
    runCheck,
    isRunning,
    readiness,
  };
}
